import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import pLimit from 'p-limit'
import { useLatest } from 'react-use'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import {
  selectCrossClientResolversInFlight,
  selectCrossClientResultsExecution,
  selectCrossClientResultsRevision,
} from '../../../../store/crossClient/crossClientResults'
import { selectCrossClientActions } from '../../../../store/crossClient/crossClientActions'
import {
  useGetCrossClientQueryExecutionResultsQuery,
  useLazyGetAllCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientQueryExecutionResultsQuery,
} from '../../../../store/crossClient/crossClient'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { CCQ_CARD_ORDER, cardFilter, type CcqCard, type CcqCardFilter, type CcqCounts } from './ccqResultView'
import { CCQ_READ_TRIES, useRetryLater } from './useRetryLater'

export const CCQ_PREVIEW_SIZE = 3

export const CCQ_RESULT_INCLUDE: ('bulkResolverAnswers' | 'localResolverAnswers')[] = ['bulkResolverAnswers', 'localResolverAnswers']

const EMPTY_PREVIEWS: Record<CcqCard, CrossClientQueryResult[]> = { matched: [], closer: [], notInRecords: [], notMatched: [], missingData: [] }

// A string key, so effects only rerun when a filter really changes and not on every polled execution object.
const useCardFiltersKey = (): string | null => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const inFlight = useAppSelector(selectCrossClientResolversInFlight)
  return execution ? JSON.stringify(Object.fromEntries(CCQ_CARD_ORDER.map((card) => [card, cardFilter(card, execution, inFlight)]))) : null
}

export const useCardFilter = (card: CcqCard | null): CcqCardFilter | null => {
  const key = useCardFiltersKey()
  return useMemo(() => (card === null || key === null ? null : (JSON.parse(key) as Record<CcqCard, CcqCardFilter>)[card]), [card, key])
}

// Selected ids travel in the query string, so a large selection is read in chunks the URL can carry.
const IDS_PER_REQUEST = 100

// A large selection would otherwise fire every chunk at once.
const ID_CHUNKS_AT_ONCE = 4

interface ResultsByIdsArgs {
  accountId: string
  executionId: string
  hasActions?: boolean
}

type GetAllResults = (args: ResultsByIdsArgs & { resultIds: string[] }) => { unwrap: () => Promise<{ data: CrossClientQueryResult[] } | undefined> }

export const collectResultsByIds = async (getAll: GetAllResults, args: ResultsByIdsArgs, ids: string[]): Promise<CrossClientQueryResult[]> => {
  const chunks = Array.from({ length: Math.ceil(ids.length / IDS_PER_REQUEST) }, (_, index) =>
    ids.slice(index * IDS_PER_REQUEST, (index + 1) * IDS_PER_REQUEST),
  )
  const limit = pLimit(ID_CHUNKS_AT_ONCE)
  const responses = await Promise.all(chunks.map((resultIds) => limit(() => getAll({ ...args, resultIds }).unwrap())))
  return responses.flatMap((response) => response?.data ?? [])
}

// Under an action filter, a new action can take selected clients off the list, and so out of the selection.
export const useCcqDeselectUnlistedOnActions = (selection: string[], hasActions: boolean | undefined, deselect: (resultIds: string[]) => void) => {
  const accountId = useAppSelector(selectAccountId)
  const executionId = useAppSelector(selectCrossClientResultsExecution)?.id
  const actionCount = useAppSelector(selectCrossClientActions).length
  const [triggerGetAll] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const checkedAtRef = useRef(actionCount)
  const hasActionsRef = useLatest(hasActions)

  useEffect(() => {
    if (checkedAtRef.current === actionCount) {
      return
    }
    checkedAtRef.current = actionCount
    if (hasActions === undefined || selection.length === 0 || !executionId) {
      return
    }
    collectResultsByIds(triggerGetAll, { accountId, executionId, hasActions }, selection)
      .then((results) => {
        if (hasActionsRef.current !== hasActions) {
          return
        }
        const listed = new Set(results.map((result) => result.id))
        const unlisted = selection.filter((id) => !listed.has(id))
        if (unlisted.length > 0) {
          deselect(unlisted)
        }
      })
      .catch(console.error)
  }, [actionCount, hasActions, selection, executionId, accountId, triggerGetAll, deselect, hasActionsRef])
}

export interface CcqPreviews {
  previews: Record<CcqCard, CrossClientQueryResult[]>
  // Cards whose previews have been read at least once, rows or not.
  loaded: Partial<Record<CcqCard, true>>
  // Cards whose read kept failing; they wait for `retry`.
  failed: Partial<Record<CcqCard, true>>
  // How many reads of each card have finished, rows or not.
  reads: Partial<Record<CcqCard, number>>
  retry: (card: CcqCard) => void
}

const withoutCards = <T>(record: Partial<Record<CcqCard, T>>, cards: CcqCard[]) =>
  Object.fromEntries(Object.entries(record).filter(([card]) => !cards.includes(card as CcqCard))) as Partial<Record<CcqCard, T>>

export const useCcqPreviews = (counts: CcqCounts, isDone: boolean, settleToken: number): CcqPreviews => {
  const accountId = useAppSelector(selectAccountId)
  const executionId = useAppSelector(selectCrossClientResultsExecution)?.id
  const filtersKey = useCardFiltersKey()
  const [trigger] = useLazyGetCrossClientQueryExecutionResultsQuery()
  const [previews, setPreviews] = useState(EMPTY_PREVIEWS)
  const [loaded, setLoaded] = useState<Partial<Record<CcqCard, true>>>({})
  const [failed, setFailed] = useState<Partial<Record<CcqCard, true>>>({})
  const [finishedReads, setFinishedReads] = useState<Partial<Record<CcqCard, number>>>({})
  const [retryRequest, setRetryRequest] = useState(0)
  // Nothing else reruns a failed read on a finished search.
  const { retryToken, retryLater } = useRetryLater()
  const seededRef = useRef<Partial<Record<CcqCard, true>>>({})
  // Bumped each time a card is read or emptied: an answer counts only while its read is still the card's latest.
  const readsRef = useRef<Partial<Record<CcqCard, number>>>({})
  const missesRef = useRef<Partial<Record<CcqCard, number>>>({})
  const refreshKey = `${isDone}-${settleToken}`
  const lastRefreshKeyRef = useRef(refreshKey)
  const cardsWithClients = CCQ_CARD_ORDER.filter((card) => counts[card] > 0).join(',')

  // A refresh clears the cards it emptied at once, so one that gets clients back waits for its rows on skeletons.
  const [clearedRefreshKey, setClearedRefreshKey] = useState(refreshKey)
  if (clearedRefreshKey !== refreshKey) {
    setClearedRefreshKey(refreshKey)
    const emptied = CCQ_CARD_ORDER.filter((card) => counts[card] === 0 && (previews[card].length > 0 || loaded[card]))
    if (emptied.length > 0) {
      setPreviews((previous) => ({ ...previous, ...Object.fromEntries(emptied.map((card) => [card, []])) }))
      setLoaded((previous) => withoutCards(previous, emptied))
    }
    if (CCQ_CARD_ORDER.some((card) => counts[card] === 0 && failed[card])) {
      setFailed((previous) =>
        withoutCards(
          previous,
          CCQ_CARD_ORDER.filter((card) => counts[card] === 0),
        ),
      )
    }
  }

  useEffect(() => {
    if (!executionId || filtersKey === null) {
      return
    }
    const isRefresh = lastRefreshKeyRef.current !== refreshKey
    lastRefreshKeyRef.current = refreshKey
    const withClients = cardsWithClients === '' ? [] : (cardsWithClients.split(',') as CcqCard[])
    const startRead = (card: CcqCard) => {
      readsRef.current[card] = (readsRef.current[card] ?? 0) + 1
      return readsRef.current[card]
    }
    const emptied = isRefresh ? CCQ_CARD_ORDER.filter((card) => !withClients.includes(card)) : []
    emptied.forEach((card) => {
      delete seededRef.current[card]
      delete missesRef.current[card]
      startRead(card)
    })
    // Previews freeze once seeded; only done and a settled move refresh them.
    const cards = withClients.filter((card) => isRefresh || !seededRef.current[card])
    if (cards.length === 0) {
      return
    }
    const reads = cards.map((card) => {
      seededRef.current[card] = true
      return startRead(card)
    })
    const isLatest = (index: number) => readsRef.current[cards[index]] === reads[index]
    const filters = JSON.parse(filtersKey) as Record<CcqCard, CcqCardFilter>
    Promise.allSettled(
      cards.map((card) =>
        trigger({ accountId, executionId, ...filters[card], include: CCQ_RESULT_INCLUDE, page: 0, size: CCQ_PREVIEW_SIZE })
          .unwrap()
          .then((response) => [card, response.data] as const),
      ),
    ).then((outcomes) => {
      outcomes.forEach((outcome) => {
        if (outcome.status === 'rejected') {
          console.error(outcome.reason)
        }
      })
      // Every card read had clients, so a read that comes back empty missed them as much as one that failed.
      const isMiss = (outcome: (typeof outcomes)[number]) => outcome.status === 'rejected' || outcome.value[1].length === 0
      const entries = outcomes.flatMap((outcome, index) => (outcome.status === 'fulfilled' && !isMiss(outcome) && isLatest(index) ? [outcome.value] : []))
      const missed = cards.filter((card, index) => isLatest(index) && isMiss(outcomes[index]))
      entries.forEach(([card]) => {
        delete missesRef.current[card]
      })
      missed.forEach((card) => {
        missesRef.current[card] = (missesRef.current[card] ?? 0) + 1
      })
      const givenUp = missed.filter((card) => (missesRef.current[card] ?? 0) >= CCQ_READ_TRIES)
      const toRetry = missed.filter((card) => !givenUp.includes(card))
      const finished = cards.filter((card, index) => isLatest(index))
      if (finished.length > 0) {
        setFinishedReads((previous) => ({ ...previous, ...Object.fromEntries(finished.map((card) => [card, (previous[card] ?? 0) + 1])) }))
      }
      if (entries.length > 0) {
        setPreviews((previous) => ({ ...previous, ...Object.fromEntries(entries) }))
        setLoaded((previous) => ({ ...previous, ...Object.fromEntries(entries.map(([card]) => [card, true])) }))
      }
      if (entries.length > 0 || givenUp.length > 0) {
        setFailed((previous) => ({
          ...withoutCards(
            previous,
            entries.map(([card]) => card),
          ),
          ...Object.fromEntries(givenUp.map((card) => [card, true])),
        }))
      }
      toRetry.forEach((card) => {
        delete seededRef.current[card]
      })
      if (toRetry.length > 0) {
        retryLater()
      }
    })
  }, [trigger, accountId, executionId, filtersKey, cardsWithClients, refreshKey, retryToken, retryLater, retryRequest])

  const retry = useCallback((card: CcqCard) => {
    delete seededRef.current[card]
    delete missesRef.current[card]
    setFailed((previous) => withoutCards(previous, [card]))
    setRetryRequest((previous) => previous + 1)
  }, [])

  return { previews, loaded, failed, reads: finishedReads, retry }
}

export const EMPTY_RESULTS: CrossClientQueryResult[] = []

// Refetches whenever `key` changes, unless frozen (a panel is open), then catches up when it thaws. A skipped
// query just takes the new key: it fetches fresh anyway once it runs.
const useRefetchOnChange = (key: number, refetch: () => unknown, isSkipped: boolean, isFrozen: boolean) => {
  const appliedRef = useRef(key)
  useEffect(() => {
    if (appliedRef.current === key) {
      return
    }
    if (isSkipped) {
      appliedRef.current = key
      return
    }
    if (isFrozen) {
      return
    }
    appliedRef.current = key
    void refetch()
  }, [key, refetch, isSkipped, isFrozen])
}

export const useRefetchOnRevision = (refetch: () => unknown, isSkipped: boolean, isFrozen: boolean) => {
  useRefetchOnChange(useAppSelector(selectCrossClientResultsRevision), refetch, isSkipped, isFrozen)
}

// The action poller doesn't bump the results revision, so lists filtered by actions follow the action count too.
export const useRefetchOnActions = (refetch: () => unknown, isSkipped: boolean, isFrozen: boolean) => {
  useRefetchOnChange(useAppSelector(selectCrossClientActions).length, refetch, isSkipped, isFrozen)
}

export const useCcqWithActionsCount = (card: CcqCard | null): number => {
  const accountId = useAppSelector(selectAccountId)
  const executionId = useAppSelector(selectCrossClientResultsExecution)?.id
  const filter = useCardFilter(card)
  const args = executionId && filter ? { accountId, executionId, ...filter, hasActions: true, page: 0, size: 1 } : skipToken
  const { currentData, refetch } = useGetCrossClientQueryExecutionResultsQuery(args)
  const isSkipped = args === skipToken
  useRefetchOnActions(refetch, isSkipped, false)
  useRefetchOnRevision(refetch, isSkipped, false)
  return currentData?.page.totalElements ?? 0
}

export const useCcqCollectCardResults = () => {
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const inFlight = useAppSelector(selectCrossClientResolversInFlight)
  const [triggerGetAll] = useLazyGetAllCrossClientQueryExecutionResultsQuery()

  return useCallback(
    async (card: CcqCard, search: string, hasActions: boolean | undefined): Promise<CrossClientQueryResult[]> => {
      if (!execution) {
        return []
      }
      const term = search.trim()
      const response = await triggerGetAll({
        accountId,
        executionId: execution.id,
        ...cardFilter(card, execution, inFlight),
        ...(term ? { search: term } : {}),
        hasActions,
      }).unwrap()
      return response?.data ?? []
    },
    [accountId, execution, inFlight, triggerGetAll],
  )
}

export interface CcqPanelResult {
  result: CrossClientQueryResult | null
  // Its read came back without it, or failed and failed again, and no shown row stands in.
  isMissing: boolean
}

export const useCcqPanelResult = (resultId: string | null, fallback: CrossClientQueryResult | null): CcqPanelResult => {
  const accountId = useAppSelector(selectAccountId)
  const executionId = useAppSelector(selectCrossClientResultsExecution)?.id
  const args = executionId && resultId ? { accountId, executionId, resultIds: [resultId], include: CCQ_RESULT_INCLUDE, page: 0, size: 1 } : skipToken
  const isSkipped = args === skipToken
  // `currentData`, not `data`: the last fetch may be the previously opened client's.
  const { currentData, isFetching, isError, refetch } = useGetCrossClientQueryExecutionResultsQuery(args)
  useRefetchOnRevision(refetch, isSkipped, false)
  const result = currentData?.data[0] ?? fallback

  // A failed read gets one more try before the client counts as missing, so one bad read doesn't drop a linked client.
  // Only a failure after the retry's own read was seen loading counts: until then the error is still the first one's.
  const { retryToken, retryLater } = useRetryLater()
  const [opened, setOpened] = useState({ resultId, retryToken, hasRetryRead: false })
  const hasRetried = retryToken > opened.retryToken
  if (opened.resultId !== resultId) {
    setOpened({ resultId, retryToken, hasRetryRead: false })
  } else if (hasRetried && isFetching && !opened.hasRetryRead) {
    setOpened({ ...opened, hasRetryRead: true })
  }
  const hasFailed = !isSkipped && result === null && isError
  useEffect(() => {
    if (hasFailed && !hasRetried) {
      retryLater()
    }
  }, [hasFailed, hasRetried, retryLater])
  useEffect(() => {
    if (hasRetried && !isSkipped) {
      refetch()
    }
  }, [hasRetried, isSkipped, refetch])

  return { result, isMissing: !isSkipped && result === null && (currentData !== undefined || (isError && opened.hasRetryRead)) }
}
